import { createContext, type ReactNode, useContext } from "react";
import { useAuth } from "react-oidc-context";
import { AUTH_BYPASS, BYPASS_USER } from "./dev-bypass";

type AuthUser = { email: string; displayName: string };

type ArtemisAuthContext = {
	user?: AuthUser;
	isAuthenticated: boolean;
	isLoading: boolean;
	login: () => Promise<void>;
	refresh: () => Promise<void>;
	logout: () => Promise<void>;
};

const ArtemisAuthContext = createContext<ArtemisAuthContext | undefined>(
	undefined,
);

export function ArtemisAuthProvider({ children }: { children: ReactNode }) {
	const auth = useAuth();

	const user: AuthUser | undefined = auth.user
		? {
				displayName: auth.user.profile.name ?? "",
				email: auth.user.profile.email ?? "",
			}
		: undefined;

	if (AUTH_BYPASS) {
		// useAuth() above still runs so hook order is identical either way; only the values
		// handed to the app change.
		const noop = async () => undefined;
		return (
			<ArtemisAuthContext.Provider
				value={{
					user: BYPASS_USER,
					isAuthenticated: true,
					isLoading: false,
					login: noop,
					refresh: noop,
					logout: noop,
				}}
			>
				{children}
			</ArtemisAuthContext.Provider>
		);
	}

	return (
		<ArtemisAuthContext.Provider
			value={{
				user,
				isAuthenticated: auth.isAuthenticated,
				isLoading: auth.isLoading,
				login: () =>
					// Cognito can't propagate logout to Google, so after signing out the Google
					// session persists and auto-selects the last account. Forcing prompt=select_account
					// (Cognito forwards it to the IdP) makes Google show the account chooser each login.
					auth.signinRedirect({
						// extraQueryParams: {
						// 	identity_provider: "Google",
						// 	prompt: "select_account",
						// },
					}),
				refresh: () => auth.signinSilent().then(() => undefined),
				logout: async () => {
					await auth.removeUser();
					const logoutUri =
						import.meta.env.VITE_AUTH_POST_LOGOUT_REDIRECT_URI ??
						`${window.location.origin}/logout`;
					const cognitoDomain = import.meta.env.VITE_AUTH_COGNITO_DOMAIN;
					const clientId = import.meta.env.VITE_AUTH_CLIENT_ID;
					window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
				},
			}}
		>
			{children}
		</ArtemisAuthContext.Provider>
	);
}

export function useArtemisAuth() {
	const context = useContext(ArtemisAuthContext);
	if (context === undefined) {
		throw new Error(
			"useArtemisAuth must be used within an ArtemisAuthProvider",
		);
	}
	return context;
}
